/** i18n bootstrap — Phase 3-i18n.
 *
 * `react-i18next` + browser-language-detection. Fourteen EU locales:
 *   - en (fallback)
 *   - fr — France
 *   - es — Spain
 *   - it — Italy
 *   - de — Germany, Austria, parts of Switzerland
 *   - nl — Netherlands + Belgium (Flemish covered by Dutch)
 *   - pt — Portugal (European Portuguese conventions)
 *   - pl — Poland
 *   - sv — Sweden
 *   - cs — Czechia
 *   - da — Denmark
 *   - fi — Finland
 *   - no — Norway (Bokmål)
 *   - ro — Romania
 *
 * Detection order: localStorage key `cvp_locale` → `navigator.language`
 * → fallback en. Switching via `useTranslation().i18n.changeLanguage`
 * persists to localStorage automatically.
 *
 * Adding a new locale: drop `<code>.json` in ./locales, import it
 * below, add it to the `resources` map + `SUPPORTED_LOCALES`. Keep
 * the JSON keys in sync across all locales — `scripts/check_i18n_drift.py`
 * + `frontend/src/test/i18n.test.tsx::locale parity` enforce this in
 * CI; missing keys silently fall back to en at runtime otherwise.
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import cs from "./locales/cs.json";
import da from "./locales/da.json";
import de from "./locales/de.json";
import el from "./locales/el.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import fi from "./locales/fi.json";
import fr from "./locales/fr.json";
import it from "./locales/it.json";
import nl from "./locales/nl.json";
import no from "./locales/no.json";
import pl from "./locales/pl.json";
import pt from "./locales/pt.json";
import ro from "./locales/ro.json";
import sv from "./locales/sv.json";


export const SUPPORTED_LOCALES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
  { code: "de", label: "Deutsch" },
  { code: "nl", label: "Nederlands" },
  { code: "pt", label: "Português" },
  { code: "pl", label: "Polski" },
  { code: "sv", label: "Svenska" },
  { code: "cs", label: "Čeština" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "no", label: "Norsk" },
  { code: "ro", label: "Română" },
  { code: "el", label: "Ελληνικά" },
] as const;


export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]["code"];


export const LOCALE_STORAGE_KEY = "cvp_locale";


void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      es: { translation: es },
      it: { translation: it },
      de: { translation: de },
      nl: { translation: nl },
      pt: { translation: pt },
      pl: { translation: pl },
      sv: { translation: sv },
      cs: { translation: cs },
      da: { translation: da },
      fi: { translation: fi },
      no: { translation: no },
      ro: { translation: ro },
      el: { translation: el },
    },
    detection: {
      // `?lang=` first so a locale carried over from the marketing site
      // (app.nexbasira.com/login?lang=es) wins on first landing and is
      // cached; then the user's saved choice; then browser Accept-Language.
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lang",
      caches: ["localStorage"],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
  });


export default i18n;
